import React from "react";
import { Badge, Button, Callout, Card, Checkbox, Field, Input, ProgressBar, Select, Textarea } from "../components/DesignSystem";
import type { HostKey, InvitationBrief, InvitationBriefAnswers, InvitationBriefCelebration, InvitationBriefRevision, InvitationBriefRevisionSummary, MehndiJoint, SequenceKey } from "../domain/types";
import {
  ACQUISITION_SOURCE_OPTIONS,
  BISMILLAH_STYLE_OPTIONS,
  CELEBRATION_PRESETS,
  COUPLE_HOSTED_TEMPLATES,
  HOSTED_BY_PARENTS_TEMPLATES,
  HOST_KEYS_REQUIRING_COUPLE_WORDING,
  HOST_KEYS_REQUIRING_PARENT_WORDING,
  HOST_OPTIONS,
  MEHNDI_JOINT_OPTIONS,
  SEQUENCE_OPTIONS,
  findTemplateText,
  renderWordingTemplate
} from "../domain/invitationBriefTemplates";
import { errorMessage } from "../services/errors";
import { usePlannerData } from "../state/PlannerDataContext";
import { LoadingState } from "./common";

const STEPS = ["Order", "Couple & Hosts", "Celebrations", "Event details", "Wording", "Formatting & extras", "Review"];
const AUTOSAVE_DELAY_MS = 900;

function emptyAnswers(): InvitationBriefAnswers {
  return { couple: {}, celebrations: [], wording: {}, formatting: {} };
}

function coupleNamesFor(answers: InvitationBriefAnswers) {
  const couple = answers.couple || {};
  return {
    brideName: couple.brideFullName || "",
    groomName: couple.groomFullName || "",
    brideParents: [couple.brideFatherName, couple.brideMotherName].filter(Boolean).join(" & "),
    groomParents: [couple.groomFatherName, couple.groomMotherName].filter(Boolean).join(" & ")
  };
}

/** Mirrors the backend's own hostNamesFor (invitation-brief.constants.ts) exactly -- the couple's
 * live preview here must resolve [[HostNames]]/[[BrideOrGroomName]] identically to what the PDF
 * renders, or the preview would silently lie about what gets printed. */
function hostNamesFor(
  hostedBy: HostKey[] | undefined,
  names: { brideName: string; groomName: string; brideParents: string; groomParents: string },
  grandparentsNames?: string
): { hostNames: string; brideOrGroomName: string } {
  const keys = hostedBy || [];
  const parts: string[] = [];
  const hasBride = keys.includes("brides_parents");
  const hasGroom = keys.includes("grooms_parents");
  if (hasBride) parts.push(names.brideParents || "the Bride's Parents");
  if (hasGroom) parts.push(names.groomParents || "the Groom's Parents");
  if (keys.includes("grandparents")) parts.push(grandparentsNames || "their grandparents");
  if (keys.includes("couple_and_parents")) {
    if (!hasBride) parts.push(names.brideParents || "the Bride's Parents");
    if (!hasGroom) parts.push(names.groomParents || "the Groom's Parents");
  }
  const hostNames = parts.length ? parts.join(" & ") : "the hosts";
  const brideOrGroomName = hasBride && !hasGroom ? names.brideName : !hasBride && hasGroom ? names.groomName : `${names.brideName} & ${names.groomName}`;
  return { hostNames, brideOrGroomName };
}

/**
 * Invitation Brief -- replaces the POB Google Form invitation-design questionnaire (Wave 4.5).
 * `standalone` distinguishes the two places this same wizard renders: `false` is the one-time
 * post-onboarding gate (App.tsx), which offers "Continue planner setup"/"I'll do this later" once
 * submitted; `true` is the permanent Account-nav entry, which only ever offers "Back to planner".
 */
export function InvitationBriefScreen({ standalone = true, onExitToPlanner, onLater }: { standalone?: boolean; onExitToPlanner?: () => void; onLater?: () => void }) {
  const { service } = usePlannerData();
  const [phase, setPhase] = React.useState<"loading" | "intro" | "wizard" | "submitted">("loading");
  const [error, setError] = React.useState<string | null>(null);
  const [brief, setBrief] = React.useState<InvitationBrief | null>(null);
  const [order, setOrder] = React.useState<{ orderNumber?: string; customerName?: string; customerEmail?: string; customerPhone?: string; invitationProduct?: string; acquisitionSource?: string }>({});
  const [answers, setAnswers] = React.useState<InvitationBriefAnswers>(emptyAnswers());
  const [step, setStep] = React.useState(0);
  const [saveStatus, setSaveStatus] = React.useState<"idle" | "saving" | "saved">("idle");
  const [submitting, setSubmitting] = React.useState(false);
  const [submitErrors, setSubmitErrors] = React.useState<string[]>([]);
  const [revisions, setRevisions] = React.useState<InvitationBriefRevisionSummary[]>([]);

  const orderRef = React.useRef(order);
  orderRef.current = order;
  const answersRef = React.useRef(answers);
  answersRef.current = answers;
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    service.invitationBrief.getDraft().then((draft) => {
      if (cancelled) return;
      setBrief(draft);
      setOrder({ orderNumber: draft.orderNumber || undefined, customerName: draft.customerName || undefined, customerEmail: draft.customerEmail || undefined, customerPhone: draft.customerPhone || undefined, invitationProduct: draft.invitationProduct || undefined, acquisitionSource: draft.acquisitionSource || undefined });
      setAnswers({ ...emptyAnswers(), ...draft.answers });
      if (draft.status === "SUBMITTED") {
        setPhase("submitted");
        service.invitationBrief.listRevisions().then((list) => !cancelled && setRevisions(list)).catch(() => {});
      } else {
        setPhase(standalone ? "wizard" : "intro");
      }
    }).catch((err) => { if (!cancelled) { setError(errorMessage(err)); setPhase("wizard"); } });
    return () => { cancelled = true; };
  }, [service, standalone]);

  const flushSave = React.useCallback(async () => {
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    setSaveStatus("saving");
    try {
      const saved = await service.invitationBrief.updateDraft({ ...orderRef.current, answers: answersRef.current });
      setBrief(saved);
      setSaveStatus("saved");
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [service]);

  const scheduleSave = React.useCallback(() => {
    setSaveStatus("saving");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { void flushSave(); }, AUTOSAVE_DELAY_MS);
  }, [flushSave]);

  React.useEffect(() => () => { if (debounceRef.current) clearTimeout(debounceRef.current); }, []);

  const updateAnswers = (patch: Partial<InvitationBriefAnswers>) => {
    setAnswers((current) => ({ ...current, ...patch }));
    scheduleSave();
  };
  const updateOrder = (patch: Partial<typeof order>) => {
    setOrder((current) => ({ ...current, ...patch }));
    scheduleSave();
  };

  const goToStep = async (next: number) => {
    await flushSave();
    setStep(next);
  };

  function computeSubmitErrors(a: InvitationBriefAnswers): string[] {
    const errors: string[] = [];
    if (!a.couple.brideFullName?.trim()) errors.push("Bride's full name is required");
    if (!a.couple.groomFullName?.trim()) errors.push("Groom's full name is required");
    if (!a.celebrations || a.celebrations.length === 0) errors.push("At least one celebration is required");
    (a.celebrations || []).forEach((c, i) => { if (!c.date) errors.push(`A date is required for ${c.label || `celebration ${i + 1}`}`); });
    return errors;
  }

  const submit = async () => {
    await flushSave();
    const errors = computeSubmitErrors(answersRef.current);
    setSubmitErrors(errors);
    if (errors.length > 0) return;
    setSubmitting(true);
    try {
      const result = await service.invitationBrief.submit();
      setBrief(result.brief);
      setAnswers({ ...emptyAnswers(), ...result.brief.answers });
      const list = await service.invitationBrief.listRevisions();
      setRevisions(list);
      setPhase("submitted");
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (phase === "loading") return <div className="pob-pad"><LoadingState label="Opening your invitation brief" /></div>;

  if (phase === "intro") {
    return (
      <div className="pob-app" style={{ display: "grid", placeItems: "center", height: "100%" }}>
        <Card padding="var(--space-6)" style={{ maxWidth: 520 }}>
          <span className="pob-eyebrow">Invitation Brief</span>
          <h1 style={{ font: "var(--type-section)", margin: "var(--space-3) 0" }}>Let's prepare your invitation details</h1>
          <p style={{ color: "var(--text-secondary)", font: "var(--type-body)" }}>
            Please tell us how you would like the names, celebrations and information on your wedding invitations. You can save your progress and return at any time.
          </p>
          {(order.orderNumber || order.customerName) && (
            <Callout tone="neutral" title={order.orderNumber ? `Order #${order.orderNumber}` : "Your order"}>{order.customerName}</Callout>
          )}
          {error && <Callout tone="error" title="Couldn't load your brief">{error}</Callout>}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: "var(--space-2)", marginTop: "var(--space-5)" }}>
            {!standalone && <Button onClick={() => onLater?.()}>I'll do this later</Button>}
            <Button variant="primary" onClick={() => setPhase("wizard")}>Get started</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (phase === "submitted" && brief) {
    return (
      <SubmittedView
        brief={brief}
        revisions={revisions}
        standalone={standalone}
        onExitToPlanner={onExitToPlanner}
        onLater={onLater}
        onEdit={() => setPhase("wizard")}
      />
    );
  }

  const names = coupleNamesFor(answers);

  return (
    <div className="pob-app pob-brief-wizard">
      <aside className="pob-brief-sidebar" style={{ padding: "var(--space-6)", borderRight: "1px solid var(--border-subtle)", display: "flex", flexDirection: "column", gap: "var(--space-5)", overflowY: "auto" }}>
        <span className="pob-eyebrow">Invitation Brief</span>
        <h1 style={{ font: "var(--type-section)" }}>Tell us your details</h1>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          {STEPS.map((label, index) => (
            <button key={label} type="button" onClick={() => void goToStep(index)} style={{
              display: "flex", alignItems: "center", gap: "var(--space-3)", height: 34, border: 0, borderRadius: "var(--radius-md)",
              background: step === index ? "var(--surface-card)" : "transparent", color: step === index ? "var(--text-primary)" : "var(--text-secondary)",
              cursor: "pointer", textAlign: "left", padding: "0 var(--space-3)", font: "var(--type-label)"
            }}>
              <span className="pob-tnum">{index + 1}</span>
              {label}
            </button>
          ))}
        </div>
        <Callout tone="neutral" title={saveStatus === "saving" ? "Saving..." : saveStatus === "saved" ? "Saved" : "Draft"}>
          Your progress is saved automatically -- come back any time.
        </Callout>
        {!standalone && <Button size="sm" onClick={() => onLater?.()}>I'll do this later</Button>}
      </aside>
      <main style={{ overflowY: "auto" }}>
        <div className="pob-pad" style={{ maxWidth: "var(--prose-max)", display: "flex", flexDirection: "column", gap: "var(--space-5)" }}>
          <div className="pob-brief-mobile-steps">
            <span className="pob-eyebrow">Invitation Brief · Step {step + 1} of {STEPS.length}: {STEPS[step]}</span>
            <ProgressBar value={step + 1} max={STEPS.length} />
            {!standalone && <Button size="sm" onClick={() => onLater?.()}>I'll do this later</Button>}
          </div>
          {error && <Callout tone="error" title="Something went wrong">{error}</Callout>}
          {step === 0 && <OrderStep order={order} update={updateOrder} />}
          {step === 1 && <CoupleHostsStep answers={answers} update={updateAnswers} />}
          {step === 2 && <CelebrationsStep answers={answers} update={updateAnswers} />}
          {step === 3 && <EventDetailsStep answers={answers} update={updateAnswers} />}
          {step === 4 && <WordingStep answers={answers} update={updateAnswers} names={names} />}
          {step === 5 && <FormattingStep answers={answers} update={updateAnswers} />}
          {step === 6 && <ReviewStep answers={answers} order={order} names={names} goToStep={(i) => void goToStep(i)} />}

          {submitErrors.length > 0 && (
            <Callout tone="error" title="A few things are needed before this can be submitted">
              <ul style={{ margin: 0, paddingLeft: "var(--space-5)" }}>{submitErrors.map((e) => <li key={e}>{e}</li>)}</ul>
            </Callout>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-2)" }}>
            <Button disabled={step === 0} onClick={() => void goToStep(step - 1)}>Back</Button>
            {step < STEPS.length - 1
              ? <Button variant="primary" onClick={() => void goToStep(step + 1)}>Continue</Button>
              : <Button variant="primary" disabled={submitting} onClick={submit}>{submitting ? "Submitting..." : "Submit invitation information"}</Button>}
          </div>
        </div>
      </main>
    </div>
  );
}

// Field wraps its children in a <label>, which is correct for a single input/select/textarea (and
// harmless around a list of Checkboxes, since each Checkbox is already its own <label>). A row of
// plain <button> toggles is different: browsers fold the entire outer label's text into the FIRST
// button's accessible name (reproduced live via Playwright's accessible-name matching -- the
// button read as "Who is hosting? Groom's Parents Grandparents... Bride's Parents" instead of just
// "Bride's Parents"). Button-toggle groups use this plain, non-<label> wrapper instead.
function ButtonGroupField({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <span className="pob-eyebrow">{label}</span>
      {children}
      {hint && <span style={{ font: "var(--type-body-sm)", fontSize: "var(--text-xs)", color: "var(--text-tertiary)" }}>{hint}</span>}
    </div>
  );
}

function Header({ eyebrow, title, description }: { eyebrow: string; title: string; description?: string }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
      <span className="pob-eyebrow">{eyebrow}</span>
      <h2 style={{ font: "var(--type-title)", color: "var(--text-primary)" }}>{title}</h2>
      {description && <p style={{ color: "var(--text-secondary)", font: "var(--type-body)" }}>{description}</p>}
    </div>
  );
}

function OrderStep({ order, update }: { order: { orderNumber?: string; customerName?: string; customerEmail?: string; customerPhone?: string; invitationProduct?: string; acquisitionSource?: string }; update: (patch: Partial<typeof order>) => void }) {
  return (
    <>
      <Header eyebrow="Step 1" title="Order information" description="These fields may already be filled in from your order -- update anything that's changed." />
      <div className="pob-grid-2">
        <Field label="Your name"><Input value={order.customerName || ""} onChange={(e) => update({ customerName: e.target.value })} /></Field>
        <Field label="Email"><Input type="email" value={order.customerEmail || ""} onChange={(e) => update({ customerEmail: e.target.value })} /></Field>
        <Field label="Phone" hint="Optional"><Input value={order.customerPhone || ""} onChange={(e) => update({ customerPhone: e.target.value })} /></Field>
        <Field label="POB order number"><Input value={order.orderNumber || ""} onChange={(e) => update({ orderNumber: e.target.value })} /></Field>
        <Field label="Invitation / design / product ordered"><Input value={order.invitationProduct || ""} onChange={(e) => update({ invitationProduct: e.target.value })} /></Field>
        <Field label="How did you hear about us?" hint="Optional"><Select placeholder="Select one" value={order.acquisitionSource || ""} onChange={(e) => update({ acquisitionSource: e.target.value })} options={ACQUISITION_SOURCE_OPTIONS} /></Field>
      </div>
    </>
  );
}

function CoupleHostsStep({ answers, update }: { answers: InvitationBriefAnswers; update: (patch: Partial<InvitationBriefAnswers>) => void }) {
  const couple = answers.couple || {};
  const hosts = couple.hosts || [];
  const hasMehndi = (answers.celebrations || []).some((c) => c.key === "mehndi");
  const toggleHost = (key: HostKey) => {
    const next = hosts.includes(key) ? hosts.filter((h) => h !== key) : [...hosts, key];
    update({ couple: { ...couple, hosts: next } });
  };
  const setCouple = (patch: Partial<typeof couple>) => update({ couple: { ...couple, ...patch } });
  return (
    <>
      <Header eyebrow="Step 2" title="Couple & Hosts" description="Enter names however you would like them to appear on the invitation. If any invitation or insert uses Urdu text, please fill in this form using Urdu." />
      <ButtonGroupField label="Who is hosting?">
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
          {HOST_OPTIONS.map((opt) => <Button key={opt.key} variant={hosts.includes(opt.key) ? "primary" : "secondary"} onClick={() => toggleHost(opt.key)}>{opt.label}</Button>)}
        </div>
      </ButtonGroupField>
      {hosts.includes("other") && <Field label="Please describe"><Input value={couple.hostsOtherText || ""} onChange={(e) => setCouple({ hostsOtherText: e.target.value })} /></Field>}
      <div className="pob-grid-2">
        <Field label="Bride's full name (as it should appear)"><Input value={couple.brideFullName || ""} onChange={(e) => setCouple({ brideFullName: e.target.value })} /></Field>
        <Field label="Groom's full name (as it should appear)"><Input value={couple.groomFullName || ""} onChange={(e) => setCouple({ groomFullName: e.target.value })} /></Field>
        {/* Grouped by side (bride's parents together, then groom's) rather than by role (both
            fathers, then both mothers) -- on mobile this grid collapses to a single column, and
            reading father/father/mother/mother in DOM order jumped confusingly between the two
            families each row. */}
        <Field label="Bride's father's name" hint="Optional"><Input value={couple.brideFatherName || ""} onChange={(e) => setCouple({ brideFatherName: e.target.value })} /></Field>
        <Field label="Bride's mother's name" hint="Optional"><Input value={couple.brideMotherName || ""} onChange={(e) => setCouple({ brideMotherName: e.target.value })} /></Field>
        <Field label="Groom's father's name" hint="Optional"><Input value={couple.groomFatherName || ""} onChange={(e) => setCouple({ groomFatherName: e.target.value })} /></Field>
        <Field label="Groom's mother's name" hint="Optional"><Input value={couple.groomMotherName || ""} onChange={(e) => setCouple({ groomMotherName: e.target.value })} /></Field>
      </div>
      {hosts.includes("grandparents") && (
        <Field label="Grandparents' names" hint="Optional -- if they should be mentioned"><Textarea rows={2} value={couple.grandparentsNames || ""} onChange={(e) => setCouple({ grandparentsNames: e.target.value })} /></Field>
      )}
      {hosts.includes("siblings") && hasMehndi && (
        <Field label="Siblings' names" hint="Optional -- if they should be mentioned"><Textarea rows={2} value={couple.siblingsNames || ""} onChange={(e) => setCouple({ siblingsNames: e.target.value })} /></Field>
      )}
    </>
  );
}

function CelebrationsStep({ answers, update }: { answers: InvitationBriefAnswers; update: (patch: Partial<InvitationBriefAnswers>) => void }) {
  const celebrations = answers.celebrations || [];
  const selectedPresetKeys = new Set(celebrations.filter((c) => c.key !== "other").map((c) => c.key));

  const togglePreset = (key: string, label: string) => {
    if (selectedPresetKeys.has(key)) {
      update({ celebrations: celebrations.filter((c) => c.key !== key) });
    } else {
      update({ celebrations: [...celebrations, { key, label }] });
    }
  };
  const addOther = () => update({ celebrations: [...celebrations, { key: "other", label: "" }] });
  const removeAt = (index: number) => update({ celebrations: celebrations.filter((_, i) => i !== index) });
  const patchAt = (index: number, patch: Partial<InvitationBriefCelebration>) => update({ celebrations: celebrations.map((c, i) => (i === index ? { ...c, ...patch } : c)) });

  return (
    <>
      <Header eyebrow="Step 3" title="Celebrations" description="Choose every celebration you'd like an invitation or insert for. You can select more than one." />
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
        {CELEBRATION_PRESETS.filter((p) => p.key !== "other").map((preset) => (
          <Button key={preset.key} variant={selectedPresetKeys.has(preset.key) ? "primary" : "secondary"} onClick={() => togglePreset(preset.key, preset.label)}>{preset.label}</Button>
        ))}
        <Button icon="plus" onClick={addOther}>Other</Button>
      </div>
      <Card eyebrow="Selected" title={`${celebrations.length} celebration${celebrations.length === 1 ? "" : "s"}`} padding="0">
        {celebrations.length === 0 && <div className="pob-pad" style={{ color: "var(--text-secondary)" }}>No celebrations selected yet.</div>}
        {celebrations.map((celebration, index) => (
          <div key={`${celebration.key}-${index}`} style={{ padding: "var(--space-4)", borderTop: index ? "1px solid var(--divider)" : 0, display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            {celebration.key === "other"
              ? <Input value={celebration.label} placeholder="Name this celebration" onChange={(e) => patchAt(index, { label: e.target.value })} />
              : <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ font: "var(--type-label)" }}>{celebration.label}</span>
                  <Button size="sm" variant="ghost" onClick={() => removeAt(index)}>Remove</Button>
                </div>}
            {celebration.key === "other" && <div><Button size="sm" variant="ghost" onClick={() => removeAt(index)}>Remove</Button></div>}
            {celebration.key === "mehndi" && (
              <ButtonGroupField label="Will Mehndi be a joint event?">
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  {MEHNDI_JOINT_OPTIONS.map((opt) => (
                    <Button key={opt.key} size="sm" variant={celebration.mehndiJoint === opt.key ? "primary" : "secondary"} onClick={() => patchAt(index, { mehndiJoint: opt.key as MehndiJoint })}>{opt.label}</Button>
                  ))}
                </div>
              </ButtonGroupField>
            )}
          </div>
        ))}
      </Card>
    </>
  );
}

function EventDetailsStep({ answers, update }: { answers: InvitationBriefAnswers; update: (patch: Partial<InvitationBriefAnswers>) => void }) {
  const celebrations = answers.celebrations || [];
  const patchAt = (index: number, patch: Partial<InvitationBriefCelebration>) => update({ celebrations: celebrations.map((c, i) => (i === index ? { ...c, ...patch } : c)) });
  if (celebrations.length === 0) {
    return (
      <>
        <Header eyebrow="Step 4" title="Event details" />
        <Callout tone="neutral" title="No celebrations yet">Go back to the Celebrations step and choose at least one to add its details here.</Callout>
      </>
    );
  }
  return (
    <>
      <Header eyebrow="Step 4" title="Event details" description="Add the date, time, venue and RSVP details for each celebration." />
      {celebrations.map((celebration, index) => (
        <Card key={`${celebration.key}-${index}`} eyebrow="Celebration" title={celebration.label || "Untitled celebration"} padding="var(--space-5)">
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
            <div className="pob-grid-2">
              <Field label="Date"><Input type="date" value={celebration.date || ""} onChange={(e) => patchAt(index, { date: e.target.value })} /></Field>
              <Field label="Start time"><Input value={celebration.startTime || ""} placeholder="e.g. 5:00 PM" onChange={(e) => patchAt(index, { startTime: e.target.value })} /></Field>
              <Field label="Venue name"><Input value={celebration.venueName || ""} onChange={(e) => patchAt(index, { venueName: e.target.value })} /></Field>
              <Field label="Complete address"><Input value={celebration.venueAddress || ""} onChange={(e) => patchAt(index, { venueAddress: e.target.value })} /></Field>
              <Field label="RSVP by" hint="Optional"><Input type="date" value={celebration.rsvpDate || ""} onChange={(e) => patchAt(index, { rsvpDate: e.target.value })} /></Field>
              <Field label="RSVP contact name" hint="Optional"><Input value={celebration.rsvpContactName || ""} onChange={(e) => patchAt(index, { rsvpContactName: e.target.value })} /></Field>
              <Field label="RSVP phone number" hint="Optional"><Input inputMode="tel" value={celebration.rsvpContactPhone || ""} onChange={(e) => patchAt(index, { rsvpContactPhone: e.target.value })} /></Field>
              <Field label="Other contact details" hint="Optional"><Input value={celebration.contactDetails || ""} onChange={(e) => patchAt(index, { contactDetails: e.target.value })} /></Field>
              <Field label="Website URL" hint="Optional"><Input value={celebration.websiteUrl || ""} onChange={(e) => patchAt(index, { websiteUrl: e.target.value })} /></Field>
              <Field label="Looking Forward / Awaiting to Welcome" hint="Optional -- names to print for this event"><Input value={celebration.lookingForwardNames || ""} onChange={(e) => patchAt(index, { lookingForwardNames: e.target.value })} /></Field>
            </div>
            <Field label="Time breakdown" hint="Optional -- keep it short to avoid clutter on the card">
              <Textarea rows={2} placeholder="e.g. Nikkah 5:00 PM, Dinner 7:00 PM" value={celebration.timeBreakdown || ""} onChange={(e) => patchAt(index, { timeBreakdown: e.target.value })} />
            </Field>
          </div>
        </Card>
      ))}
    </>
  );
}

function WordingStep({ answers, update, names }: { answers: InvitationBriefAnswers; update: (patch: Partial<InvitationBriefAnswers>) => void; names: { brideName: string; groomName: string; brideParents: string; groomParents: string } }) {
  const wording = answers.wording || {};
  const celebrations = answers.celebrations || [];
  const setWording = (patch: Partial<typeof wording>) => update({ wording: { ...wording, ...patch } });
  const patchCelebration = (index: number, patch: Partial<InvitationBriefCelebration>) => update({ celebrations: celebrations.map((c, i) => (i === index ? { ...c, ...patch } : c)) });
  const customWording = wording.customWording || [];
  const setCustomWordingText = (scope: string, text: string) => {
    const existing = customWording.find((c) => c.scope === scope);
    const next = existing ? customWording.map((c) => (c.scope === scope ? { ...c, text } : c)) : [...customWording, { scope, text }];
    setWording({ customWording: next });
  };
  const wholeWeddingText = customWording.find((c) => c.scope === "whole_wedding")?.text || "";

  return (
    <>
      <Header eyebrow="Step 5" title="Wording" description="Who's hosting can differ by celebration (e.g. siblings hosting Mehndi, parents hosting Baraat) -- choose hosting and wording for each one below." />
      <ButtonGroupField label="Would you like Bismillah on the invitation?">
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <Button variant={wording.bismillah ? "primary" : "secondary"} onClick={() => setWording({ bismillah: true })}>Yes</Button>
          <Button variant={wording.bismillah === false ? "primary" : "secondary"} onClick={() => setWording({ bismillah: false, bismillahStyle: undefined })}>No</Button>
        </div>
      </ButtonGroupField>
      {wording.bismillah && (
        <ButtonGroupField label="Style">
          <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
            {BISMILLAH_STYLE_OPTIONS.map((opt) => <Button key={opt.key} variant={wording.bismillahStyle === opt.key ? "primary" : "secondary"} onClick={() => setWording({ bismillahStyle: opt.key })}>{opt.label}</Button>)}
          </div>
        </ButtonGroupField>
      )}

      {celebrations.map((celebration, index) => {
        const hostedBy = celebration.hostedBy || [];
        const toggleHost = (key: HostKey) => {
          const next = hostedBy.includes(key) ? hostedBy.filter((h) => h !== key) : [...hostedBy, key];
          patchCelebration(index, { hostedBy: next });
        };
        const templateKeys = celebration.wordingTemplateKeys || [];
        const toggleTemplate = (key: string) => {
          const next = templateKeys.includes(key) ? templateKeys.filter((k) => k !== key) : [...templateKeys, key];
          patchCelebration(index, { wordingTemplateKeys: next });
        };
        const showParentWording = hostedBy.some((h) => HOST_KEYS_REQUIRING_PARENT_WORDING.includes(h));
        const showCoupleWording = hostedBy.some((h) => HOST_KEYS_REQUIRING_COUPLE_WORDING.includes(h));
        const { hostNames, brideOrGroomName } = hostNamesFor(hostedBy, names, answers.couple?.grandparentsNames);
        const tokens = { ...names, eventName: celebration.label || "celebration", hostNames, brideOrGroomName };

        return (
          <Card key={`${celebration.key}-${index}`} eyebrow="Celebration" title={celebration.label || "Untitled celebration"} padding="var(--space-5)">
            <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
              <ButtonGroupField label={`Who is hosting ${celebration.label || "this celebration"}?`}>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
                  {HOST_OPTIONS.map((opt) => <Button key={opt.key} size="sm" variant={hostedBy.includes(opt.key) ? "primary" : "secondary"} onClick={() => toggleHost(opt.key)}>{opt.label}</Button>)}
                </div>
              </ButtonGroupField>

              {showParentWording && (
                <Field label="Hosted-by-parents wording" hint="Select any that apply">
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    {HOSTED_BY_PARENTS_TEMPLATES.map((template) => (
                      <Checkbox
                        key={template.key}
                        checked={templateKeys.includes(template.key)}
                        onChange={() => toggleTemplate(template.key)}
                        label={template.key === "other" ? "Other" : renderWordingTemplate(template.text, tokens)}
                      />
                    ))}
                  </div>
                </Field>
              )}

              {showCoupleWording && (
                <Field label="Couple-hosted wording" hint="Select any that apply">
                  <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                    {COUPLE_HOSTED_TEMPLATES.map((template) => (
                      <Checkbox
                        key={template.key}
                        checked={templateKeys.includes(template.key)}
                        onChange={() => toggleTemplate(template.key)}
                        label={template.key === "other" ? "Other" : renderWordingTemplate(template.text, tokens)}
                      />
                    ))}
                  </div>
                </Field>
              )}

              {templateKeys.includes("other") && (
                <Field label="Please describe">
                  <Textarea value={celebration.wordingOtherText || ""} onChange={(e) => patchCelebration(index, { wordingOtherText: e.target.value })} />
                </Field>
              )}

              <Field label={`Custom wording -- ${celebration.label}`} hint="Optional, specific to this celebration only">
                <Textarea value={customWording.find((c) => c.scope === celebration.label)?.text || ""} onChange={(e) => setCustomWordingText(celebration.label, e.target.value)} />
              </Field>
            </div>
          </Card>
        );
      })}

      <Field label="Custom wording" hint="If you have specific wording or phrases you want to include across every invitation">
        <Textarea value={wholeWeddingText} onChange={(e) => setCustomWordingText("whole_wedding", e.target.value)} />
      </Field>
    </>
  );
}

function FormattingStep({ answers, update }: { answers: InvitationBriefAnswers; update: (patch: Partial<InvitationBriefAnswers>) => void }) {
  const formatting = answers.formatting || {};
  const setFormatting = (patch: Partial<typeof formatting>) => update({ formatting: { ...formatting, ...patch } });
  const qr = formatting.locationQr || {};
  const celebrations = answers.celebrations || [];
  const setQr = (patch: Partial<typeof qr>) => setFormatting({ locationQr: { ...qr, ...patch } });

  return (
    <>
      <Header eyebrow="Step 6" title="Formatting & extras" />
      <ButtonGroupField label="Sequence of details on the invitation">
        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-2)" }}>
          {SEQUENCE_OPTIONS.map((opt) => <Button key={opt.key} variant={formatting.sequence === opt.key ? "primary" : "secondary"} onClick={() => setFormatting({ sequence: opt.key as SequenceKey })}>{opt.label}</Button>)}
        </div>
      </ButtonGroupField>
      {formatting.sequence === "other" && <Field label="Please describe"><Input value={formatting.sequenceOtherText || ""} onChange={(e) => setFormatting({ sequenceOtherText: e.target.value })} /></Field>}

      <ButtonGroupField label="Would you like a QR code linking to the venue location?" hint="Subject to available space on the invitation.">
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          <Button variant={qr.enabled ? "primary" : "secondary"} onClick={() => setQr({ enabled: true })}>Yes</Button>
          <Button variant={qr.enabled === false ? "primary" : "secondary"} onClick={() => setQr({ enabled: false, urls: {} })}>No</Button>
        </div>
      </ButtonGroupField>
      {/* Every celebration gets a URL field directly -- no "which celebrations" opt-in checkbox.
          A couple wanting a QR code almost always wants it for every event, not a subset. */}
      {qr.enabled && (
        <Field label="Location URLs">
          <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
            {celebrations.map((celebration) => (
              <div key={celebration.label} style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
                <span className="pob-eyebrow">{celebration.label}</span>
                <Input placeholder="Location URL (e.g. Google Maps link)" value={qr.urls?.[celebration.label] || ""} onChange={(e) => setQr({ urls: { ...(qr.urls || {}), [celebration.label]: e.target.value } })} />
              </div>
            ))}
          </div>
        </Field>
      )}

      <Field label="Additional comments" hint="Optional -- anything else our design team should know"><Textarea rows={4} value={answers.additionalComments || ""} onChange={(e) => update({ additionalComments: e.target.value })} /></Field>
    </>
  );
}

function ReviewStep({ answers, order, names, goToStep }: { answers: InvitationBriefAnswers; order: Record<string, string | undefined>; names: { brideName: string; groomName: string; brideParents: string; groomParents: string }; goToStep: (step: number) => void }) {
  const rsvpSummary = (celebration: InvitationBriefCelebration) => [
    celebration.rsvpDate ? `RSVP by ${celebration.rsvpDate}` : null,
    celebration.rsvpContactName,
    celebration.rsvpContactPhone
  ].filter(Boolean).join(" · ");

  return (
    <>
      <Header eyebrow="Step 7" title="Review" description="Check everything below, then submit your invitation information to Pockets of Beige." />
      <Card eyebrow="Order" title="Order & customer" action={<Button size="sm" onClick={() => goToStep(0)}>Edit</Button>} padding="var(--space-5)">
        <div className="pob-grid-2">
          <Summary label="Order #" value={order.orderNumber} />
          <Summary label="Customer" value={order.customerName} />
          <Summary label="Invitation / design" value={order.invitationProduct} />
        </div>
      </Card>
      <Card eyebrow="Couple & Hosts" title={`${names.brideName || "Bride"} & ${names.groomName || "Groom"}`} action={<Button size="sm" onClick={() => goToStep(1)}>Edit</Button>} padding="var(--space-5)">
        <div className="pob-grid-2">
          <Summary label="Hosted by" value={(answers.couple.hosts || []).map((key) => HOST_OPTIONS.find((opt) => opt.key === key)?.label || key).join(", ")} />
          <Summary label="Bride's father" value={answers.couple.brideFatherName} />
          <Summary label="Groom's father" value={answers.couple.groomFatherName} />
        </div>
      </Card>
      <Card eyebrow="Celebrations" title={`${(answers.celebrations || []).length} celebration(s)`} action={<Button size="sm" onClick={() => goToStep(2)}>Edit</Button>} padding="0">
        {(answers.celebrations || []).map((c, i) => (
          <div key={`${c.label}-${i}`} style={{ padding: "var(--space-3) var(--space-4)", borderTop: i ? "1px solid var(--divider)" : 0 }}>
            <span style={{ font: "var(--type-label)" }}>{c.label}</span>
            <span style={{ marginLeft: "var(--space-3)", color: "var(--text-secondary)" }}>{c.date} {c.startTime} {c.venueName}</span>
            {rsvpSummary(c) && <div style={{ marginTop: 4, color: "var(--text-secondary)", font: "var(--type-body-sm)" }}>{rsvpSummary(c)}</div>}
          </div>
        ))}
      </Card>
      <Card eyebrow="Wording & Formatting" title="Style" action={<Button size="sm" onClick={() => goToStep(4)}>Edit</Button>} padding="var(--space-5)">
        <div className="pob-grid-2">
          <Summary label="Bismillah" value={answers.wording.bismillah ? "Yes" : "No"} />
          <Summary label="Sequence" value={SEQUENCE_OPTIONS.find((o) => o.key === answers.formatting.sequence)?.label} />
        </div>
      </Card>
      {answers.additionalComments && (
        <Card eyebrow="Additional comments" title="Notes" action={<Button size="sm" onClick={() => goToStep(5)}>Edit</Button>} padding="var(--space-5)">
          <p style={{ margin: 0 }}>{answers.additionalComments}</p>
        </Card>
      )}
    </>
  );
}

function Summary({ label, value }: { label: string; value?: React.ReactNode }) {
  return <div><span className="pob-eyebrow">{label}</span><div style={{ marginTop: 4, font: "var(--type-label)" }}>{value || "—"}</div></div>;
}

function SubmittedView({ brief, revisions, standalone, onExitToPlanner, onLater, onEdit }: {
  brief: InvitationBrief;
  revisions: InvitationBriefRevisionSummary[];
  standalone: boolean;
  onExitToPlanner?: () => void;
  onLater?: () => void;
  onEdit: () => void;
}) {
  const { service } = usePlannerData();
  const [showDetails, setShowDetails] = React.useState(false);
  const [downloading, setDownloading] = React.useState<number | null>(null);
  const [diffFor, setDiffFor] = React.useState<number | null>(null);
  const [diff, setDiff] = React.useState<Array<{ field: string; from: string; to: string }>>([]);
  const [betaActive, setBetaActive] = React.useState(false);

  React.useEffect(() => {
    let cancelled = false;
    service.weddings.getAccessStatus().then((status) => { if (!cancelled) setBetaActive(Boolean(status.beta)); }).catch(() => {});
    return () => { cancelled = true; };
  }, [service]);

  const download = async (version: number) => {
    setDownloading(version);
    try {
      const url = await service.invitationBrief.getRevisionPdfUrl(version);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      // best-effort -- surfaced implicitly by the button staying available to retry
    } finally {
      setDownloading(null);
    }
  };

  const viewDiff = async (version: number) => {
    if (diffFor === version) { setDiffFor(null); return; }
    const entries = await service.invitationBrief.diffRevisions(version, version - 1);
    setDiff(entries);
    setDiffFor(version);
  };

  return (
    <div className="pob-app" style={{ display: "grid", placeItems: "center", height: "100%", overflowY: "auto" }}>
      <Card padding="var(--space-6)" style={{ maxWidth: 560, margin: "var(--space-6) 0" }}>
        <Badge tone="success">Invitation details submitted</Badge>
        <h1 style={{ font: "var(--type-section)", margin: "var(--space-3) 0" }}>Pockets of Beige has received your information</h1>
        <p style={{ color: "var(--text-secondary)" }}>Invitation Brief v{brief.currentVersion} · Submitted {brief.submittedAt ? new Date(brief.submittedAt).toLocaleDateString() : ""}</p>

        {betaActive ? (
          <Callout tone="success" title="Private Beta">You've received complimentary Full Planner access for this wedding.</Callout>
        ) : !standalone ? (
          <Callout tone="neutral" title="Complimentary POB Planner">
            Your Pockets of Beige order also includes complimentary access to our wedding planning workspace.
          </Callout>
        ) : null}

        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", margin: "var(--space-4) 0" }}>
          <Button onClick={() => setShowDetails((v) => !v)}>{showDetails ? "Hide" : "View"} submitted details</Button>
          <Button onClick={() => void download(brief.currentVersion)} disabled={downloading === brief.currentVersion}>{downloading === brief.currentVersion ? "Preparing..." : "Download PDF"}</Button>
          <Button onClick={onEdit}>Edit brief</Button>
        </div>

        {showDetails && (
          <Card eyebrow="Latest submission" title={`${brief.answers.couple.brideFullName || ""} & ${brief.answers.couple.groomFullName || ""}`} padding="var(--space-4)">
            {(brief.answers.celebrations || []).map((c) => (
              <div key={c.label} style={{ padding: "var(--space-2) 0", borderTop: "1px solid var(--divider)" }}>
                <strong>{c.label}</strong> — {c.date} {c.startTime} {c.venueName}
                {[c.rsvpDate ? `RSVP by ${c.rsvpDate}` : null, c.rsvpContactName, c.rsvpContactPhone].filter(Boolean).length > 0 && (
                  <div style={{ marginTop: 4, color: "var(--text-secondary)", font: "var(--type-body-sm)" }}>
                    {[c.rsvpDate ? `RSVP by ${c.rsvpDate}` : null, c.rsvpContactName, c.rsvpContactPhone].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {revisions.length > 1 && (
          <Card eyebrow="Version history" title="Previous submissions" padding="0" style={{ marginTop: "var(--space-4)" }}>
            {revisions.map((rev, i) => (
              <div key={rev.id} style={{ padding: "var(--space-3) var(--space-4)", borderTop: i ? "1px solid var(--divider)" : 0 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>Invitation Brief v{rev.version} · Submitted {new Date(rev.submittedAt).toLocaleDateString()}</span>
                  <div style={{ display: "flex", gap: "var(--space-2)" }}>
                    {rev.version > 1 && <Button size="sm" onClick={() => void viewDiff(rev.version)}>{diffFor === rev.version ? "Hide changes" : "What changed"}</Button>}
                    <Button size="sm" onClick={() => void download(rev.version)}>{downloading === rev.version ? "..." : "PDF"}</Button>
                  </div>
                </div>
                {diffFor === rev.version && (
                  <div style={{ marginTop: "var(--space-2)", display: "flex", flexDirection: "column", gap: 4 }}>
                    {diff.length === 0 && <span style={{ color: "var(--text-secondary)" }}>No differences.</span>}
                    {diff.map((d) => <span key={d.field} style={{ font: "var(--type-body-sm)" }}>{d.field}: {d.from || "(empty)"} → {d.to || "(empty)"}</span>)}
                  </div>
                )}
              </div>
            ))}
          </Card>
        )}

        {!standalone && (
          <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-5)" }}>
            <Button variant="primary" onClick={onExitToPlanner}>Open my planner</Button>
            <Button onClick={onLater}>I'll do this later</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
